/**
 * Created by simon zhao on 6/27/2022.
 * Description:
 * DiagramRelationshipAutomationDialog Component
 * ------ maintenance history ------
 * Updated by Daniel Wang on 5/22/2023, Added new control type - User
 */
import { KeyValue } from '@angular/common';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, HostListener } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';
import { FieldConfig } from '../../../../components/work-flow/field/field.model';
import { businessConstants } from '../../../../constants/business.constants';
import { AppState } from '../../../../redux';
import { relationshipTypeSelector } from '../../../../redux/reducers/relationship-type.reducer';
import { AppConfig } from '../../../../tamalelibs/models/app-config.model';
import { AutomationRelationshipItem, AutomationRelationshipModel, OWNER_PREFIX } from '../../../../tamalelibs/models/automation.model';
import { EntityBrief } from '../../../../tamalelibs/models/entity-brief.model';
import { EntryType } from '../../../../tamalelibs/models/entry-type.model';
import { RelationshipType } from '../../../../tamalelibs/models/relationship-type.model';
import { WorkflowActionTypes } from '../../../../tamalelibs/redux/actions/workflow.actions';
import { currentWorkflowAllFieldsSelector } from '../../../../tamalelibs/redux/reducers/workflow.reducer';
import { EntityService } from '../../../../tamalelibs/services/entity.service';
import { WorkflowService } from '../../../../tamalelibs/services/workflow.service';
import { AlertWindowService } from '../../../alert-window/alert-window.service';
import { MultiSelDropdownConfig } from '../../../multi-sel-dropdown-filter/multi-sel-dropdown-filter.model';
import { DiagramAutomationNoteDialogService } from '../diagram-note-automation-dialog/diagram-note-automation-dialog.service';
import { AutomationRelationshipItemViewModel, DiagramRelationshipDialogOptions, DiagramRelationshipDialogResult, EntityFilterArgument, ENTITY_SOURCE_TYPES, ID_DELIMITER, MultipleEntitySelectionViewModel, SingleSelectionViewModel } from './diagram-relationship-automation-dialog.model';
import { DiagramRelationshipDialogService } from './diagram-relationship-automation-dialog.service';

@Component({
    selector: 'tam-diagram-relationship-automation-dialog',
    templateUrl: './diagram-relationship-automation-dialog.component.html',
    styleUrls: ['./diagram-relationship-automation-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiagramRelationshipAutomationDialogComponent implements OnInit, OnDestroy {
    //#region public properties
    /**
    * all field controls in current workflow.
    */
    allFieldControls: Array<FieldConfig> = [];

    /**
     * all relationship types.
     */
    allRelationshipTypes: Array<RelationshipType> = [];

    /**
    * the input parameters for current dialog instance.
    */
    dialogOptions: DiagramRelationshipDialogOptions;

    get entitySourceTypes(): Array<{ value: string, text: string }> {
        return ENTITY_SOURCE_TYPES;
    }

    /**
     * a flag indicating whether the save button is clicked after any change is made.
    */
    isSaveBtnClicked: boolean;

    /**
     * a flag indicating whether the dialog is loading.
     */
    isDialogLoading: boolean;

    /**
     * a flag indicating whether current relationship config is valid.
     */
    get isRelationshipConfigValid(): boolean {
        const isRelationInvalidFunc = (r: AutomationRelationshipItemViewModel) => {
            const isParentInvalid = !r.parentDataSource.selectedValue || !r.parentDataSource.selectedValue.value;
            const isRelationTypeInvalid = !r.relationshipTypeDataSource.selectedValue || !r.relationshipTypeDataSource.selectedValue.value;
            const isChildInvalid = r.selectedChildEntitySrc.value === 'entity' ? (!r.childEntityDataSource.selectedEntityItems || r.childEntityDataSource.selectedEntityItems.length === 0) : (!r.childFieldDataSource.data || r.childFieldDataSource.data.filter(fItem => fItem.checked).length === 0);
            return isParentInvalid || isRelationTypeInvalid || isChildInvalid;
        };
        return !this.relationshipSettings.some(relation => isRelationInvalidFunc(relation));
    }

    /**
    * a flag indicating whether current dialog is visible.
    */
    opened: boolean;

    /**
     * A collection of current relationship config.
     */
    relationshipSettings: Array<AutomationRelationshipItemViewModel> = [];

    /**
     * a list of submitters of all workflow tasks.
     */
    submitterOfTaskList: Array<KeyValue<string, string>> = [];

    //#endregion

    //#region private properties

    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _destroySubscriptions: Array<Subscription> = [];

    private _filterEntityHandler$: Subject<EntityFilterArgument> = new Subject();

    /**
     * a flag indicating whether the dialog content is pristine.
     */
    private _isDialogPristine = true;

    /**
     * the subscription array that would be unsubscribed on closing.
     */
    private _openSubscriptions: Array<Subscription> = [];

    private _preloadedEntities: Array<EntityBrief> = [];

    //#endregion

    constructor(
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _diagramAutomationDialogService: DiagramAutomationNoteDialogService,
        private _dialogService: DiagramRelationshipDialogService,
        private _entityService: EntityService,
        private _store: Store<AppState>,
        private _workflowService: WorkflowService,
    ) { }

    //#region public methods

    @HostListener('window:beforeunload', ['$event'])
    beforeunloadHandler($event: any) {
        if (this.opened) {
            $event.returnValue = true;
        }
    }

    beforeEntitiesDropdownOpen(e: any, ref: MultiSelectComponent): void {
        if (ref && ref.wrapper && ref.wrapper.nativeElement) {
            const searchInput = ref.wrapper.nativeElement.querySelector('input', '.k-multiselect-wrap .k-floatwrap');
            if (searchInput && !searchInput.value) {
                e.preventDefault();
            }
        }
    }

    /**
     * Event hanlder for the filter change event of single-selection dropdown.
     * @param keyword the keyword, with which the single selection dropdown's candidates are filtered.
     * @param dataSourceVM the data source of the given single selection dropdown.
     */
    handleSingleFilterChanged(keyword: string, dataSourceVM: SingleSelectionViewModel<KeyValue<string, string>>) {
        if (keyword) {
            dataSourceVM.filteredDataSource = dataSourceVM.rawDataSource.filter(ks => ks.key.toLowerCase().includes(keyword.toLowerCase()));
        } else {
            dataSourceVM.filteredDataSource = dataSourceVM.rawDataSource.slice();
        }
    }

    /**
     * Event handler for the filter change event of the given multi-selection dropdown.
     * @param keyword the keyword, with which the multi-selection entity dropdown's candidates are filtered.
     * @param entityDataSource the data source of the given multi-selection entity dropdown.
     */
    handleEntitiesFilter(keyword: string, entityDataSource: MultipleEntitySelectionViewModel, ref: MultiSelectComponent) {
        const entityArgument = new EntityFilterArgument();
        entityArgument.keyword = keyword;
        entityArgument.entityDataSource = entityDataSource;
        entityArgument.dropdownRef = ref;
        ref.toggle(false);
        this._filterEntityHandler$.next(entityArgument);
    }

    /**
     * Determines whether the child source is field-source.
     * @param selectedSource the selected value of the child source dropdown.
     * @returns a flag indicating whether the selected value is field-source.
     */
    isChildSourceField(selectedSource: { value: string }) {
        return selectedSource && selectedSource.value === 'field';
    }

    ngOnInit() {
        this._destroySubscriptions.push(
            this._dialogService.dialogOpen$.pipe(filter(action => !this.opened),
            ).subscribe((dialogOptions: DiagramRelationshipDialogOptions) => {
                this.opened = true;
                this.dialogOptions = dialogOptions;
                this._reset();
                this._beforeInitCandidates().subscribe(isSucceed => {
                    this._initObservers();
                    this._initCandidates();
                    this._changeDetectorRef.detectChanges();
                });
            }));
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    onRelationOperationChange(relationItem: AutomationRelationshipItemViewModel, isAppend: boolean) {
        if (isAppend !== relationItem.isAppend) {
            relationItem.isAppend = isAppend;
            this._afterDialogChange();
        }
    }

    onDeleteRow(relationItem: AutomationRelationshipItemViewModel) {
        const indexOfDeletion = this.relationshipSettings.indexOf(relationItem);
        if (indexOfDeletion >= 0) {
            this.relationshipSettings.splice(indexOfDeletion, 1);
            this._afterDialogChange();
            this._changeDetectorRef.detectChanges();
        }
    }

    /**
     * event handler for the click event of cancel buttonn;
     * @param $event the event argument
     */
    onCancelBtnClick($event: Event) {
        if ($event.stopPropagation) {
            $event.stopPropagation();
        }

        if (!this._isDialogPristine) {
            // pop up the confirmation dialog only when the dialog content has been changed.
            const subscription = this._alertWindow.warn(businessConstants.alertWindow.warnTitle, [businessConstants.alertWindow.warnContent])
                .subscribe((result: boolean) => {
                    if (result) {
                        this._close();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._close();
        }
    }

    onClickSaveBtn($event: Event) {
        this.isSaveBtnClicked = true;
        if (this.isRelationshipConfigValid) {
            let automationRecord = new AutomationRelationshipModel();
            automationRecord = this._generateAutomationRecord();
            automationRecord.name = this._workflowService.getAutomationCardContent(automationRecord, null);
            automationRecord.enable = true;
            automationRecord.attachedNodeId = this.dialogOptions.nodeId;
            // add the new automation node to store.
            if (automationRecord) {
                if (this.dialogOptions.isUpdate) {
                    this._store.dispatch({
                        type: WorkflowActionTypes.UPDATE_AUTOMATION_NODE,
                        payload: [automationRecord]
                    });
                } else {
                    this._store.dispatch({
                        type: WorkflowActionTypes.ADD_AUTOMATION_NODE,
                        payload: [automationRecord]
                    });
                }
            }
            this._close(automationRecord);
        }
    }

    onAddRowClick() {
        this.relationshipSettings.push(this._addRelationshipRow(this.allFieldControls, this.allRelationshipTypes));
        this._afterDialogChange();
    }

    onFieldSelectionChange(e: any) {
        this._afterDialogChange();
    }

    onChildEntitySelectionChange(e: any, relationItem: AutomationRelationshipItemViewModel) {
        if (relationItem.childEntityDataSource.selectedEntityItems.length > 0) {
            relationItem.selectedChildEntityOrFieldIds = relationItem.childEntityDataSource.selectedEntityItems.map(eb => eb.id).join(ID_DELIMITER);
        } else {
            relationItem.selectedChildEntityOrFieldIds = '';
        }

        this._afterDialogChange();
    }

    onChildSourceSelectionChange(e: any, relationItem: AutomationRelationshipItemViewModel) {
        relationItem.selectedChildEntityOrFieldIds = '';
        relationItem.childEntityDataSource.selectedEntityItems = [];
        relationItem.childFieldDataSource.clear$.next(true);
    }

    searchingEntities(keyword: string, entityDataSource: MultipleEntitySelectionViewModel, dropdownRef: any) {
        entityDataSource.isLoading = true;
        this._changeDetectorRef.detectChanges();
        const handleEntitySearchResult = response => {
            const entities = response ? this._entityService.mapEntityBriefList(response) : [];
            entityDataSource.filteredDataSource = entities;
            entityDataSource.isLoading = false;
            if (entities && entities.length > 0 && dropdownRef) {
                dropdownRef.toggle(true);
            }
            this._changeDetectorRef.detectChanges();
        };
        this._destroySubscriptions.push(this._entityService.getEntityListBySearchTextQuick(1, AppConfig.searchingPageSize, keyword).pipe(
            take(1)
        ).subscribe(
            res => {
                handleEntitySearchResult(res);
            }, error => {
                handleEntitySearchResult(null);
            })
        );
    }

    //#endregion

    //#region private methods
    private _afterDialogChange() {
        this._isDialogPristine = false;
        this.isSaveBtnClicked = false;
    }

    private _addRelationshipRow(controls: Array<FieldConfig>, relationTypes: Array<RelationshipType>, existingRelation?: AutomationRelationshipItem): AutomationRelationshipItemViewModel {
        const rowVM = AutomationRelationshipItemViewModel.createNewRelationItem(controls, relationTypes, this._preloadedEntities, this.submitterOfTaskList.map(submitterKs => ({ id: submitterKs.value, name: submitterKs.key, checked: false })), existingRelation);
        this._openSubscriptions.push(
            rowVM.childFieldDataSource.onChangeValue$.subscribe((selectedFields: Array<MultiSelDropdownConfig>) => {
                rowVM.selectedChildEntityOrFieldIds = selectedFields.map(f => f.id).join(ID_DELIMITER);
                this._afterDialogChange();
            })
        );
        return rowVM;
    }

    private _beforeInitCandidates(): Observable<boolean> {
        return new Observable(
            o => {
                if (this.dialogOptions.isUpdate && this.dialogOptions.existingRelations) {
                    const entityIdsToQuery = [];
                    this.dialogOptions.existingRelations.relationships.forEach(relation => {
                        if (relation && relation.child && relation.child.type === 'entity') {
                            entityIdsToQuery.push(...relation.child.ids.split(ID_DELIMITER));
                        }
                    });
                    this._entityService.getEntityListByIdsQuick(entityIdsToQuery)
                        .pipe(
                            take(1),
                            map((response) =>
                                this._entityService.mapEntityBriefList(response)
                            )
                        ).subscribe(
                            entities => {
                                this._preloadedEntities.push(...entities);
                                o.next(true);
                                o.complete();
                            },
                            err => {
                                o.next(false);
                                o.complete();
                            }
                        );
                } else {
                    o.next(true);
                    o.complete();
                }
            }
        );

    }

    private _close(relationModel: AutomationRelationshipModel = null) {
        this.opened = false;
        const dialogResult = new DiagramRelationshipDialogResult();
        dialogResult.isConfirmed = !!relationModel;
        if (relationModel) {
            dialogResult.relationResult = relationModel;
        }
        dialogResult.isUpdate = this.dialogOptions.isUpdate;
        dialogResult.nodeId = this.dialogOptions.nodeId;
        this._dialogService.dialogClose$.next(dialogResult);
        this._openSubscriptions.forEach(sub => sub.unsubscribe());
        this._openSubscriptions = [];
        this._changeDetectorRef.detectChanges();
    }

    /**
     * generate a new automation record for saving.
     * @returns an automation record for persisting.
     */
    private _generateAutomationRecord() {
        const relationModel = AutomationRelationshipItemViewModel.toRelationshipModel(this.relationshipSettings);
        if (!this.dialogOptions.isUpdate) {
            relationModel.id = this._workflowService.getIdFromServerInStore(this._store);
        } else {
            relationModel.id = this.dialogOptions.existingRelations.id;
        }
        return relationModel;
    }

    private _reset() {
        this.allFieldControls = [];
        this.allRelationshipTypes = [];
        this.isSaveBtnClicked = false;
        this._isDialogPristine = true;
        this.relationshipSettings = [];
        this._preloadedEntities = [];
    }

    private _initObservers() {
        this._openSubscriptions.push(this._filterEntityHandler$.pipe(
            debounceTime(250),
            filter(filterArg => filterArg && filterArg.keyword && filterArg.keyword.trim().length > 0)
        ).subscribe(filterArg => {
            this.searchingEntities(filterArg.keyword, filterArg.entityDataSource, filterArg.dropdownRef);
        }));
    }

    private _initCandidates() {
        this.submitterOfTaskList = this.dialogOptions.taskArray.map(kv => {
            return {
                key: OWNER_PREFIX.key + kv.key,
                value: OWNER_PREFIX.value + kv.value
            };
        });
        this._openSubscriptions.push(
            this._store.pipe(
                select(currentWorkflowAllFieldsSelector),
            ).subscribe((fieldBriefs: Array<FieldConfig>) => {
                this.allFieldControls = [];
                if (fieldBriefs && fieldBriefs.length) {
                    this.allFieldControls.push(...fieldBriefs);
                }
            }),
            this._store.pipe(select(relationshipTypeSelector),
                take(1)
            ).subscribe((types: Array<RelationshipType>) => { // relationship candiate list.
                if (types && types.length > 0) {
                    this.allRelationshipTypes.push(...types);
                }

                if (this.dialogOptions.isUpdate && this.dialogOptions.existingRelations) {
                    this.dialogOptions.existingRelations.relationships.forEach(relation => {
                        this.relationshipSettings.push(this._addRelationshipRow(this.allFieldControls, this.allRelationshipTypes, relation));
                    });
                } else {
                    this.relationshipSettings.push(this._addRelationshipRow(this.allFieldControls, this.allRelationshipTypes));
                }
            }));
    }
    //#endregion
}
